import type { Product } from '../types';

/** Produto ou departamento sem número vai para o fim. */
export function rankOrdem(value: number | undefined | null): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : Number.POSITIVE_INFINITY;
}

export function productLabel(p: Product): string {
  const raw = p.name as Product['name'] | string | undefined;
  if (typeof raw === 'string') return raw;
  return raw?.pt || raw?.en || '';
}

export function compareByOrdemThenName(a: Product, b: Product): number {
  const oa = rankOrdem(a.ordem);
  const ob = rankOrdem(b.ordem);
  if (oa !== ob) return oa - ob;
  return productLabel(a).localeCompare(productLabel(b), 'pt-BR');
}

/** Destaques, depois ordem do departamento, depois ordem do produto, depois nome. */
export function compareStoreProducts(
  a: Product,
  b: Product,
  departmentOrdem?: Record<string, number> | null
): number {
  const destaque = Number(b.destaque === true) - Number(a.destaque === true);
  if (destaque !== 0) return destaque;
  const da = rankOrdem(departmentOrdem?.[(a.category || '').trim()]);
  const db = rankOrdem(departmentOrdem?.[(b.category || '').trim()]);
  if (da !== db) return da - db;
  return compareByOrdemThenName(a, b);
}

export function compareDepartments(
  a: string,
  b: string,
  departmentOrdem?: Record<string, number> | null,
  fallbackIndex?: (name: string) => number
): number {
  const ra = rankOrdem(departmentOrdem?.[a]);
  const rb = rankOrdem(departmentOrdem?.[b]);
  if (ra !== rb) return ra - rb;
  if (fallbackIndex) {
    const ia = fallbackIndex(a);
    const ib = fallbackIndex(b);
    if (ia !== ib) return ia - ib;
  }
  return a.localeCompare(b, 'pt-BR');
}

const ORDEM_BASE = 1_000_000;

/** Troca dois vizinhos gravando só o número `ordem` de cada um. */
export function swapNeighborOrdem(
  length: number,
  index: number,
  dir: -1 | 1,
  read: (index: number) => number | undefined
): { index: number; ordem: number }[] | null {
  const next = index + dir;
  if (index < 0 || next < 0 || next >= length) return null;
  const current = read(index) ?? ORDEM_BASE + index;
  const neighbor = read(next) ?? ORDEM_BASE + next;
  return [
    { index, ordem: current === neighbor ? neighbor - 1 : neighbor },
    { index: next, ordem: current },
  ];
}
